import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, TrendingUp, MessageSquare, X, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Search, ChevronDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Available markets - exported for use in other components
export const markets = [{
  id: 'hamburg-hafencity',
  name: 'EDEKA Center Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3
}, {
  id: 'berlin-kreuzberg',
  name: 'EDEKA Neukauf Berlin-Kreuzberg',
  region: 'Minden-Hannover',
  rating: 3.8
}, {
  id: 'muenchen-schwabing',
  name: 'EDEKA München-Schwabing',
  region: 'Südbayern',
  rating: 4.6
}, {
  id: 'koeln-ehrenfeld',
  name: 'EDEKA Köln-Ehrenfeld',
  region: 'Rhein-Ruhr',
  rating: 3.5
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'EDEKA Frankfurt-Sachsenhausen',
  region: 'Hessenring',
  rating: 4.4
}, {
  id: 'dortmund-innenstadt',
  name: 'EDEKA Dortmund-Innenstadt',
  region: 'Rhein-Ruhr',
  rating: 3.9
}, {
  id: 'leipzig-sued',
  name: 'EDEKA Center Leipzig-Süd',
  region: 'Nordbayern',
  rating: 4.5
}, {
  id: 'stuttgart-west',
  name: 'EDEKA Stuttgart-West',
  region: 'Südwest',
  rating: 4.2
}, {
  id: 'freiburg-zentrum',
  name: 'EDEKA Freiburg-Zentrum',
  region: 'Südwest',
  rating: 4.1
}, {
  id: 'kiel-zentrum',
  name: 'EDEKA Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4
}];
// Mock data per market
const marketData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  'hamburg-hafencity': {
    positive: 245,
    neutral: 82,
    negative: 38,
    total: 365,
    avgRating: 4.3,
    trend: 0.12
  },
  'berlin-kreuzberg': {
    positive: 156,
    neutral: 98,
    negative: 76,
    total: 330,
    avgRating: 3.8,
    trend: -0.08
  },
  'muenchen-schwabing': {
    positive: 312,
    neutral: 65,
    negative: 23,
    total: 400,
    avgRating: 4.6,
    trend: 0.18
  },
  'koeln-ehrenfeld': {
    positive: 124,
    neutral: 89,
    negative: 87,
    total: 300,
    avgRating: 3.5,
    trend: -0.15
  },
  'frankfurt-sachsenhausen': {
    positive: 278,
    neutral: 72,
    negative: 30,
    total: 380,
    avgRating: 4.4,
    trend: 0.1
  },
  'dortmund-innenstadt': {
    positive: 168,
    neutral: 95,
    negative: 57,
    total: 320,
    avgRating: 3.9,
    trend: 0.05
  },
  'leipzig-sued': {
    positive: 298,
    neutral: 68,
    negative: 24,
    total: 390,
    avgRating: 4.5,
    trend: 0.22
  },
  'stuttgart-west': {
    positive: 234,
    neutral: 78,
    negative: 38,
    total: 350,
    avgRating: 4.2,
    trend: 0.08
  },
  'freiburg-zentrum': {
    positive: 215,
    neutral: 82,
    negative: 43,
    total: 340,
    avgRating: 4.1,
    trend: 0.06
  },
  'kiel-zentrum': {
    positive: 267,
    neutral: 70,
    negative: 28,
    total: 365,
    avgRating: 4.4,
    trend: 0.14
  }
};
// Topic clusters data per market
const marketTopicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    date: string;
    rating: number;
  }>;
}>> = {
  'hamburg-hafencity': [{
    name: 'Frische & Qualität',
    size: 85,
    sentiment: 'positive',
    comments: [{
      text: 'Die Obst- und Gemüseabteilung ist immer top frisch!',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Fleischtheke in der Umgebung.',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Freundlichkeit',
    size: 62,
    sentiment: 'positive',
    comments: [{
      text: 'Personal ist immer freundlich und hilfsbereit.',
      date: '2024-01-28',
      rating: 5
    }]
  }, {
    name: 'Wartezeiten',
    size: 45,
    sentiment: 'negative',
    comments: [{
      text: 'Zu wenig Kassen offen, lange Wartezeiten.',
      date: '2024-01-26',
      rating: 2
    }]
  }, {
    name: 'Sortiment',
    size: 38,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 32,
    sentiment: 'neutral',
    comments: []
  }, {
    name: 'Parkplatz',
    size: 28,
    sentiment: 'negative',
    comments: []
  }],
  'berlin-kreuzberg': [{
    name: 'Wartezeiten',
    size: 72,
    sentiment: 'negative',
    comments: [{
      text: 'Samstags ist es unmöglich hier einzukaufen.',
      date: '2024-01-27',
      rating: 1
    }]
  }, {
    name: 'Sortiment',
    size: 58,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 45,
    sentiment: 'neutral',
    comments: []
  }, {
    name: 'Sauberkeit',
    size: 38,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Freundlichkeit',
    size: 35,
    sentiment: 'neutral',
    comments: []
  }],
  'muenchen-schwabing': [{
    name: 'Frische & Qualität',
    size: 95,
    sentiment: 'positive',
    comments: [{
      text: 'Beste Fleischtheke in der Umgebung, sehr gute Beratung.',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Freundlichkeit',
    size: 78,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Sortiment',
    size: 65,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Sauberkeit',
    size: 42,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 35,
    sentiment: 'neutral',
    comments: []
  }]
};
// Default topic clusters for markets without specific data
const defaultTopicClusters = [{
  name: 'Frische & Qualität',
  size: 65,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Freundlichkeit',
  size: 52,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Wartezeiten',
  size: 38,
  sentiment: 'negative' as const,
  comments: []
}, {
  name: 'Sortiment',
  size: 45,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Preise',
  size: 32,
  sentiment: 'neutral' as const,
  comments: []
}];
// 12-month trend data (generic, scaled per market)
const generateTrendData = (avgRating: number, trend: number) => {
  const months = ['Feb 23', 'Mär 23', 'Apr 23', 'Mai 23', 'Jun 23', 'Jul 23', 'Aug 23', 'Sep 23', 'Okt 23', 'Nov 23', 'Dez 23', 'Jan 24'];
  const baseRating = avgRating - trend * 6;
  return months.map((month, i) => ({
    month,
    rating: Math.round((baseRating + trend * i / 2 + (Math.random() * 0.1 - 0.05)) * 100) / 100,
    reviews: Math.floor(25 + Math.random() * 15)
  }));
};
// Simple custom treemap component
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-879">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-880">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-881">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-882">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
interface MarktStimmungPageProps {
  selectedMarket: string | null;
  onMarketChange: (marketId: string | null) => void;
}
export function MarktStimmungPage({
  selectedMarket,
  onMarketChange
}: MarktStimmungPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const filteredMarkets = markets.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.region.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectedMarketInfo = markets.find(m => m.id === selectedMarket);
  const currentData = selectedMarket ? marketData[selectedMarket] || marketData['hamburg-hafencity'] : null;
  const currentTopics = selectedMarket ? marketTopicClusters[selectedMarket] || defaultTopicClusters : [];
  const currentTrend = selectedMarket && currentData ? generateTrendData(currentData.avgRating, currentData.trend) : [];
  const totalSentiments = currentData ? currentData.positive + currentData.neutral + currentData.negative : 0;
  const positivePercent = currentData ? Math.round(currentData.positive / totalSentiments * 100) : 0;
  const neutralPercent = currentData ? Math.round(currentData.neutral / totalSentiments * 100) : 0;
  const negativePercent = currentData ? Math.round(currentData.negative / totalSentiments * 100) : 0;
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  // If no market selected, show market selection
  if (!selectedMarket) {
    return <div className="p-6 space-y-6 overflow-y-auto h-screen" data-id="element-883">
        <Card className="!p-6" data-id="element-884">
          <div className="text-center mb-6" data-id="element-885">
            <div className="w-16 h-16 rounded-2xl bg-edeka-blue/10 flex items-center justify-center mx-auto mb-4" data-id="element-886">
              <Store size={32} className="text-edeka-blue" data-id="element-887" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-888">
              Markt auswählen
            </h2>
            <p className="text-gray-500 text-sm" data-id="element-889">
              Wählen Sie einen Markt aus, um das Stimmungsbarometer anzuzeigen
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-4" data-id="element-890">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-891" />
            <input type="text" placeholder="Markt suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-edeka-blue/20 focus:border-edeka-blue" data-id="element-892" />
          </div>

          {/* Market List */}
          <div className="space-y-2 max-h-96 overflow-y-auto" data-id="element-893">
            {filteredMarkets.map(market => <button key={market.id} onClick={() => onMarketChange(market.id)} className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-edeka-blue hover:bg-edeka-blue/5 transition-all text-left" data-id="element-894">
                <div className="flex items-center gap-3" data-id="element-895">
                  <div className="w-10 h-10 rounded-full bg-edeka-blue/10 flex items-center justify-center" data-id="element-896">
                    <Store size={18} className="text-edeka-blue" data-id="element-897" />
                  </div>
                  <div data-id="element-898">
                    <div className="font-medium text-gray-900" data-id="element-899">
                      {market.name}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-900">{market.region}</div>
                  </div>
                </div>
                <div className="text-right" data-id="element-901">
                  <div className="flex items-center gap-1" data-id="element-902">
                    {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < Math.floor(market.rating) ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-903">
                        ★
                      </span>)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-904">
                    {market.rating.toFixed(1)} Sterne
                  </div>
                </div>
              </button>)}
          </div>
        </Card>
      </div>;
  }
  return <div className="p-6 space-y-6 overflow-y-auto h-screen" data-id="element-905">
      {/* Market Selector */}
      <Card className="!p-4" data-id="element-906">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-907">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-908">
            <Store size={16} data-id="element-909" />
            <span data-id="element-910">Markt:</span>
          </div>

          <div className="relative flex-1 max-w-md" data-id="element-911">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full flex items-center justify-between px-4 py-2.5 bg-edeka-blue text-white rounded-lg shadow-md" data-id="element-912">
              <div className="flex items-center gap-2" data-id="element-913">
                <Store size={16} data-id="element-914" />
                <span className="font-medium" data-id="element-915">{selectedMarketInfo?.name}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-916" />
            </button>

            {isDropdownOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-64 overflow-y-auto" data-id="element-917">
                <div className="p-2" data-id="element-918">
                  <input type="text" placeholder="Suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-edeka-blue/20" data-id="element-919" />
                </div>
                {filteredMarkets.map(market => <button key={market.id} onClick={() => {
              onMarketChange(market.id);
              setIsDropdownOpen(false);
              setSearchQuery('');
            }} className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left ${market.id === selectedMarket ? 'bg-edeka-blue/5' : ''}`} data-id="element-920">
                    <Store size={14} className="text-gray-400" data-id="element-921" />
                    <div className="flex-1" data-id="element-922">
                      <div className="text-sm font-medium text-gray-900" data-id="element-923">
                        {market.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-924">
                        {market.region}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-925">
                      {market.rating.toFixed(1)} ★
                    </div>
                  </button>)}
              </div>}
          </div>

          <button onClick={() => onMarketChange(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-926">
            Markt wechseln
          </button>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-927">
        <KPICard title="Durchschnittliche Bewertung" value={currentData?.avgRating.toFixed(1) || '0'} change={currentData && currentData.trend > 0 ? `+${currentData.trend.toFixed(2)}` : currentData?.trend.toFixed(2) || '0'} trend={currentData && currentData.trend >= 0 ? 'up' : 'down'} icon={<TrendingUp size={18} data-id="element-929" />} subtext="vs. Vormonat" data-id="element-928" />
        <KPICard title="Gesamtbewertungen" value={currentData?.total.toLocaleString('de-DE') || '0'} change="+12%" trend="up" icon={<MessageSquare size={18} data-id="element-931" />} subtext="diese Periode" data-id="element-930" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} change={`${currentData?.positive.toLocaleString('de-DE') || 0}`} trend="up" icon={<ThumbsUp size={18} data-id="element-933" />} subtext="Kommentare" data-id="element-932" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} change={`${currentData?.negative.toLocaleString('de-DE') || 0}`} trend="down" icon={<ThumbsDown size={18} data-id="element-935" />} subtext="Kommentare" data-id="element-934" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-936">
        {/* Absolute Resonanz */}
        <Card data-id="element-937">
          <div className="p-6" data-id="element-938">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-939">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-940">
              Verteilung der Sentiment-Analyse
            </p>

            {/* Stacked Bar */}
            <div className="mb-6" data-id="element-941">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-942">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-943">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-944">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#FFD301'
              }} className="flex items-center justify-center" data-id="element-945">
                  {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-946">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-947">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-948">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            {/* Legend with numbers */}
            <div className="space-y-3" data-id="element-949">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-950">
                <div className="flex items-center gap-3" data-id="element-951">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-952">
                    <ThumbsUp size={18} className="text-white" data-id="element-953" />
                  </div>
                  <div data-id="element-954">
                    <div className="font-semibold text-gray-900" data-id="element-955">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-956">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-957">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-958">
                    {currentData?.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-959">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fff9db'
            }} data-id="element-960">
                <div className="flex items-center gap-3" data-id="element-961">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-962">
                    <Minus size={18} className="text-gray-800" data-id="element-963" />
                  </div>
                  <div data-id="element-964">
                    <div className="font-semibold text-gray-900" data-id="element-965">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-966">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-967">
                  <div className="text-2xl font-bold" style={{
                  color: '#b39500'
                }} data-id="element-968">
                    {currentData?.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-969">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-970">
                <div className="flex items-center gap-3" data-id="element-971">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-972">
                    <ThumbsDown size={18} className="text-white" data-id="element-973" />
                  </div>
                  <div data-id="element-974">
                    <div className="font-semibold text-gray-900" data-id="element-975">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-976">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-977">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-978">
                    {currentData?.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-979">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster Treemap */}
        <Card data-id="element-980">
          <div className="p-6" data-id="element-981">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-982">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-983">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>

            <div className="h-80" data-id="element-984">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-985" />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-986">
              <div className="flex items-center gap-2" data-id="element-987">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-988"></div>
                <span className="text-xs text-gray-600" data-id="element-989">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-990">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-991"></div>
                <span className="text-xs text-gray-600" data-id="element-992">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-993">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-994"></div>
                <span className="text-xs text-gray-600" data-id="element-995">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-996">
        <div className="p-6" data-id="element-997">
          <div className="flex items-center justify-between mb-6" data-id="element-998">
            <div data-id="element-999">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1000">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1001">
                Durchschnittliche Bewertung der letzten 12 Monate
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-1002">
              <div className="flex items-center gap-2" data-id="element-1003">
                <div className="w-3 h-3 rounded-full bg-edeka-blue" data-id="element-1004"></div>
                <span className="text-gray-600" data-id="element-1005">Ø Bewertung</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-1006">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1007">
              <LineChart data={currentTrend} data-id="element-1008">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1009" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1010" />
                <YAxis domain={[3.0, 5.0]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => value.toFixed(1)} data-id="element-1011" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number) => [value.toFixed(2), 'Ø Bewertung']} data-id="element-1012" />
                <Line type="monotone" dataKey="rating" stroke="#003051" strokeWidth={3} dot={{
                fill: '#003051',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#FFD200',
                stroke: '#003051',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-1013" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Trend Summary */}
          {currentTrend.length > 0 && <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-1014">
              <div className="flex items-center gap-4" data-id="element-1015">
                <div className="flex items-center gap-2" data-id="element-1016">
                  <TrendingUp size={18} style={{
                color: currentData && currentData.trend >= 0 ? '#639754' : '#E03C32'
              }} data-id="element-1017" />
                  <span className="text-sm text-gray-600" data-id="element-1018">
                    <span className="font-semibold" style={{
                  color: currentData && currentData.trend >= 0 ? '#639754' : '#E03C32'
                }} data-id="element-1019">
                      {currentData && currentData.trend >= 0 ? '+' : ''}
                      {((currentTrend[11]?.rating - currentTrend[0]?.rating) / currentTrend[0]?.rating * 100).toFixed(1)}
                      %
                    </span>{' '}
                    {currentData && currentData.trend >= 0 ? 'Verbesserung' : 'Rückgang'}{' '}
                    im Jahresvergleich
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500" data-id="element-1020">
                Aktuell:{' '}
                <span className="font-semibold text-gray-900" data-id="element-1021">
                  {currentTrend[11]?.rating.toFixed(2)}
                </span>{' '}
                Sterne
              </div>
            </div>}
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-1022">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-1023">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1024">
              {/* Dialog Header */}
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-1025">
                <div className="flex items-center justify-between" data-id="element-1026">
                  <div data-id="element-1027">
                    <div className="flex items-center gap-3" data-id="element-1028">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-1029">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-1030">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1031">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-1032">
                    <X size={20} data-id="element-1033" />
                  </button>
                </div>
              </div>

              {/* Dialog Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-1034">
                <div className="space-y-4" data-id="element-1035">
                  {selectedTopic.comments.map((comment: any, index: number) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1036">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-1037">
                        <div className="flex items-center gap-2" data-id="element-1038">
                          <MapPin size={14} className="text-edeka-blue" data-id="element-1039" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-1040">
                            {selectedMarketInfo?.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1041">
                          <div className="flex" data-id="element-1042">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-1043">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-1044">
                            <Calendar size={12} data-id="element-1045" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-1046">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-1047">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-1048">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}